import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { MetricsCards } from '@/components/dashboard/MetricsCards';
import { AdminClientsTable } from '@/components/admin/AdminClientsTable';
import { AddBrokerModal } from '@/components/admin/AddBrokerModal';
import { RemoveBrokerModal } from '@/components/admin/RemoveBrokerModal';
import { AddClientModal } from '@/components/admin/AddClientModal';
import { BrokerageAssetsPanel } from '@/components/admin/BrokerageAssetsPanel';
import { ProposalControlsPanel } from '@/components/admin/ProposalControlsPanel';
import { ChatModal } from '@/components/dashboard/ChatModal';
import { Button } from '@/components/ui/button';
import { UserPlus, UserMinus, Users } from 'lucide-react';

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  num_guests: number;
  broker_id: string;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
  brokers?: {
    full_name: string;
    email: string;
  };
}

const BrokerageAdminDashboard = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showAddBroker, setShowAddBroker] = useState(false);
  const [showRemoveBroker, setShowRemoveBroker] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const { user, loading: authLoading, profile } = useAuth();

  // Fetch ALL conversations for metrics
  const { data: allConversations = [] } = useQuery({
    queryKey: ['admin-all-conversations', profile?.brokerage_id],
    queryFn: async () => {
      console.log('Fetching all conversations for admin with profile:', profile);

      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          clients (
            full_name,
            email,
            phone,
            preferred_budget_eur,
            taste_notes
          ),
          brokers (
            full_name,
            email
          )
        `)
        .eq('brokerage_id', profile?.brokerage_id)
        .or('status.is.null,status.neq.archived')
        .order('last_updated', { ascending: false });
      
      if (error) {
        console.error('Error fetching admin conversations:', error);
        throw error;
      }
      
      return (data || []).map(item => {
        let parsedTranscript: ConversationMessage[] = [];
        
        if (item.transcript) {
          try {
            if (Array.isArray(item.transcript)) {
              parsedTranscript = item.transcript.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                role: msg.role || '',
                content: msg.content || '',
                datetime: msg.datetime
              }));
            } else if (typeof item.transcript === 'string') {
              const parsed = JSON.parse(item.transcript);
              if (Array.isArray(parsed)) {
                parsedTranscript = parsed.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                  role: msg.role || '',
                  content: msg.content || '',
                  datetime: msg.datetime
                }));
              }
            }
          } catch (e) {
            console.error('Error parsing transcript for conversation', item.id, ':', e);
            parsedTranscript = [];
          }
        }
        
        return {
          id: item.id,
          client_id: item.client_id,
          budget_eur: item.budget_eur,
          start_date: item.start_date,
          end_date: item.end_date,
          destination: item.destination,
          summary: item.summary,
          transcript: parsedTranscript,
          closing_probability: item.closing_probability,
          last_updated: item.last_updated,
          broker_notes: item.broker_notes,
          status: item.status || 'active',
          active: item.active,
          num_guests: item.num_guests,
          broker_id: item.broker_id,
          clients: item.clients ? {
            full_name: item.clients.full_name || '',
            email: item.clients.email || '',
            phone: item.clients.phone || '',
            preferred_budget_eur: item.clients.preferred_budget_eur || 0,
            taste_notes: item.clients.taste_notes || ''
          } : undefined,
          brokers: item.brokers ? {
            full_name: item.brokers.full_name || '',
            email: item.brokers.email || ''
          } : undefined
        };
      });
    },
    enabled: !!user && !!profile && profile.role === 'brokerage_admin',
    refetchInterval: 2000
  });

  // Remove duplicates by client_id and sort by most recent last_updated
  const uniqueConversations = React.useMemo(() => {
    const clientMap = new Map<string, Conversation>();
    
    allConversations.forEach(conv => {
      const existingConv = clientMap.get(conv.client_id);
      if (!existingConv || new Date(conv.last_updated) > new Date(existingConv.last_updated)) {
        clientMap.set(conv.client_id, conv);
      }
    });
    
    return Array.from(clientMap.values()).sort((a, b) => {
      const dateA = new Date(a.last_updated || 0);
      const dateB = new Date(b.last_updated || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [allConversations]);

  const handleViewChat = (conversation: Conversation) => {
    console.log('Opening chat modal for conversation:', conversation.id);
    setSelectedConversation(conversation);
  };

  // If auth is still loading, show loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not brokerage admin
  if (profile?.role !== 'brokerage_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Access Denied</h1>
          <p className="text-slate-600">You need brokerage admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100">
      <DashboardHeader />
      
      <div className="container mx-auto px-4 py-5 sm:px-6 sm:py-8">
        <MetricsCards conversations={uniqueConversations} />
        
        {/* Admin Action Buttons */}
        <div className="mt-8 mb-6">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Brokerage Management</h3>
            <div className="grid gap-3 sm:flex sm:flex-wrap sm:gap-4">
              <Button
                onClick={() => setShowAddBroker(true)}
                className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <UserPlus className="h-4 w-4" />
                <span>Add Broker</span>
              </Button>
              
              <Button
                onClick={() => setShowRemoveBroker(true)}
                variant="outline"
                className="flex items-center space-x-2 border-red-200 text-red-700 hover:bg-red-50"
              >
                <UserMinus className="h-4 w-4" />
                <span>Remove Broker</span>
              </Button>
              
              <Button
                onClick={() => setShowAddClient(true)}
                variant="outline"
                className="flex items-center space-x-2 border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Users className="h-4 w-4" />
                <span>Add Client</span>
              </Button>
            </div>
          </div>
        </div>

        <BrokerageAssetsPanel brokerageId={profile?.brokerage_id || ''} />
        <ProposalControlsPanel
          brokerageId={profile?.brokerage_id || ''}
          conversations={uniqueConversations}
        />
        
        <div className="mt-8">
          <AdminClientsTable 
            conversations={uniqueConversations}
            onViewChat={handleViewChat}
          />
        </div>
      </div>

      {selectedConversation && (
        <ChatModal 
          conversation={selectedConversation} 
          onClose={() => setSelectedConversation(null)} 
        />
      )}

      {showAddBroker && (
        <AddBrokerModal
          isOpen={showAddBroker}
          onClose={() => setShowAddBroker(false)}
          brokerageId={profile?.brokerage_id || ''}
        />
      )}

      {showRemoveBroker && (
        <RemoveBrokerModal
          isOpen={showRemoveBroker}
          onClose={() => setShowRemoveBroker(false)}
          brokerageId={profile?.brokerage_id || ''}
        />
      )}

      {showAddClient && (
        <AddClientModal
          isOpen={showAddClient}
          onClose={() => setShowAddClient(false)}
          brokerageId={profile?.brokerage_id || ''}
        />
      )}
    </div>
  );
};

export default BrokerageAdminDashboard;
